import type { WarningsTolerance } from '@lmc/ts-common'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import type { ReactElement } from 'react'
import type {
  ClockInOutAction,
  ClockInOutType
} from 'screens/Rostering/hooks/useClockInOut'
import {
  CanceledIcon,
  DoneIcon,
  DueIcon,
  InProgressIcon,
  OverdueIcon,
  PendingIcon
} from 'screens/Rostering/RotaBuilder/common/components/icons'
import {
  type ClockIn,
  type ClockInTeamMember,
  GeolocationStatus,
  type RotaEventInstance
} from 'screens/Rostering/types'

dayjs.extend(isoWeek)

export const GEOLOCATION_WARNING_STATUSES: GeolocationStatus[] = [
  GeolocationStatus.OUTSIDE_GEOFENCE,
  GeolocationStatus.UNAVAILABLE,
  GeolocationStatus.ADMIN_OVERRIDE
]

export const GEOLOCATION_MESSAGE_KEYS: Record<
  GeolocationStatus,
  { in: string; out: string }
> = {
  [GeolocationStatus.OUTSIDE_GEOFENCE]: {
    in: 'CLOCKED_IN_FAR_FROM_VISIT_ADDRESS',
    out: 'CLOCKED_OUT_FAR_FROM_VISIT_ADDRESS'
  },
  [GeolocationStatus.UNAVAILABLE]: {
    in: 'LOCATION_COULD_NOT_BE_VERIFIED',
    out: 'LOCATION_COULD_NOT_BE_VERIFIED'
  },
  [GeolocationStatus.ADMIN_OVERRIDE]: {
    in: 'MANUAL_CLOCK_IN_BY_ADMIN',
    out: 'MANUAL_CLOCK_OUT_BY_ADMIN'
  },
  [GeolocationStatus.WITHIN_GEOFENCE]: { in: '', out: '' }
}

type GetStatusArgs = {
  clockInOutDetails: ClockIn | undefined
}

type GetStatusReturn = {
  label: string
  icon: ReactElement
}

export function getStatus({
  clockInOutDetails
}: GetStatusArgs): GetStatusReturn {
  const status = clockInOutDetails?.status

  const statusIconMap = {
    cancelled: { label: 'CANCELLED', icon: <CanceledIcon /> },
    pending: { label: 'PENDING', icon: <PendingIcon /> },
    due: { label: 'DUE', icon: <DueIcon /> },
    overdue: { label: 'OVERDUE', icon: <OverdueIcon /> },
    inProgress: { label: 'IN_PROGRESS', icon: <InProgressIcon /> },
    done: { label: 'COMPLETED', icon: <DoneIcon /> }
  }

  if (status) {
    type StatusIconMapKey = keyof typeof statusIconMap

    const result = statusIconMap[status as StatusIconMapKey]

    if (result) {
      return result
    }
  }

  if (clockInOutDetails?.clockedIn) {
    return statusIconMap.inProgress
  }

  return statusIconMap.pending
}

export function getWarnings(clockInWarnings: ClockInTeamMember['warnings']) {
  return [
    ...getClockInWarnings(clockInWarnings),
    ...getClockOutWarnings(clockInWarnings)
  ]
}

export function getClockInWarnings(
  clockInWarnings: ClockInTeamMember['warnings']
) {
  const warnings: string[] = []

  if (clockInWarnings?.clockInEarly ?? 0) {
    warnings.push('CLOCK_IN_EARLY')
  }

  if (clockInWarnings?.clockInLate ?? 0) {
    warnings.push('CLOCK_IN_LATE')
  }

  if (clockInWarnings?.clockInLocationMismatch === true) {
    warnings.push('CLOCK_IN_LOCATION_MISMATCH')
  }

  return warnings
}

export function getClockOutWarnings(
  clockOutWarnings: ClockInTeamMember['warnings']
) {
  const warnings: string[] = []

  if (clockOutWarnings?.clockOutLate ?? 0) {
    warnings.push('CLOCK_OUT_LATE')
  }

  if (clockOutWarnings?.clockOutEarly ?? 0) {
    warnings.push('CLOCK_OUT_EARLY')
  }

  if (clockOutWarnings?.clockOutLocationMismatch === true) {
    warnings.push('CLOCK_OUT_LOCATION_MISMATCH')
  }

  return warnings
}

const LOCATION_WARNING_KEYS = new Set([
  'CLOCK_IN_LOCATION_MISMATCH',
  'CLOCK_OUT_LOCATION_MISMATCH'
])

export function getTimeWarnings(warnings: string[] | undefined): string[] {
  return warnings?.filter((w) => !LOCATION_WARNING_KEYS.has(w)) ?? []
}

export function getDurationWarnings(
  durationWarnings: ClockInTeamMember['warnings'],
  warningsTolerance: WarningsTolerance | undefined
) {
  const warnings = []

  if (
    (durationWarnings?.clockInMismatch ?? 0) >
    (warningsTolerance?.durationMismatched ?? 0)
  ) {
    warnings.push('DURATION_MISMATCH')
  }

  return warnings
}

export default getDurationWarnings

type GetPrefilledValuesArgs = {
  clockInOutDetails: ClockIn | undefined
  action: ClockInOutAction
  type: ClockInOutType
  timezone: string
  revisionDetail: RotaEventInstance
}

type GetPrefilledValuesReturn = {
  date: dayjs.Dayjs
  time: dayjs.Dayjs
  note: string
}

export function getPrefilledValues({
  clockInOutDetails,
  action,
  type,
  timezone,
  revisionDetail
}: GetPrefilledValuesArgs): GetPrefilledValuesReturn {
  if (action === 'edit') {
    if (type === 'in') {
      const dateTime = dayjs(
        clockInOutDetails?.clockInTime || clockInOutDetails?.clockInTimeSystem
      ).tz(timezone)
      return {
        date: dateTime,
        time: dateTime,
        note: clockInOutDetails?.clockInNotes || ''
      }
    }

    if (type === 'out') {
      const dateTime = dayjs(
        clockInOutDetails?.clockOutTime || clockInOutDetails?.clockOutTimeSystem
      ).tz(timezone)
      return {
        date: dateTime,
        time: dateTime,
        note: clockInOutDetails?.clockOutNotes || ''
      }
    }
  }

  return {
    date: dayjs(revisionDetail?.startTime).utc(),
    time: dayjs(
      type === 'in' ? revisionDetail?.startTime : revisionDetail?.endTime
    ).utc(),
    note: ''
  }
}
